import { useState, useCallback } from 'react';
import { toast } from '../components/toast';
import { useLoadingState } from './useLoadingState';
import {
  getInventoryAssignments,
  createInventoryAssignment,
  updateInventoryAssignment,
  deleteInventoryAssignment,
  getInventoryAssignmentDetails,
  returnInventoryAssignment,
  getInventoryAssignmentFilterOptions,
  getInventoryAssignmentStats,
} from '../services/api';

export const useInventoryAssignmentOperations = () => {
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });
  const [inventoryAssignments, setInventoryAssignments] = useState([]);
  const [stats, setStats] = useState({
    total_assignments: 0,
    active_assignments: 0,
    returned_assignments: 0,
    overdue_assignments: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});

  const loadInventoryAssignments = useCallback(
    async (page, rowsPerPage, searchTerm, employeeFilter, inventoryItemFilter, statusFilter) => {
      await withLoading(async () => {
        try {
          const response = await getInventoryAssignments(
            page + 1, // API expects 1-based pagination
            rowsPerPage,
            searchTerm,
            employeeFilter,
            inventoryItemFilter,
            statusFilter
          );

          if (response.data) {
            setInventoryAssignments(response.data.results || []);
            setTotalCount(response.data.pagination_info?.count || response.data.count || 0);

            // Update stats if included in response
            if (response.data.stats) {
              setStats(response.data.stats);
            }

            // Update filter options if included in response
            if (response.data.filter_options) {
              setFilterOptions(response.data.filter_options);
            }
          }
        } catch (error) {
          console.error('Error loading inventory assignments:', error);
          toast.error('Failed to load inventory assignments');
        }
      });
    },
    [withLoading]
  );

  const loadFilterOptions = useCallback(async () => {
    try {
      const response = await getInventoryAssignmentFilterOptions();
      if (response.data) {
        setFilterOptions(response.data.filter_options || response.data || {});
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await getInventoryAssignmentStats();
      if (response.data) {
        setStats(response.data.stats || response.data || {});
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const createInventoryAssignmentRecord = useCallback(async (assignmentData) => {
    try {
      await createInventoryAssignment(assignmentData);
      toast.success('Inventory assignment created successfully!');
      return true;
    } catch (error) {
      console.error('Error creating inventory assignment:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to create inventory assignment';
      toast.error(errorMessage);
      return false;
    }
  }, []);

  const updateInventoryAssignmentRecord = useCallback(async (assignmentId, assignmentData) => {
    try {
      await updateInventoryAssignment(assignmentId, assignmentData);
      toast.success('Inventory assignment updated successfully!');
      return true;
    } catch (error) {
      console.error('Error updating inventory assignment:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to update inventory assignment';
      toast.error(errorMessage);
      return false;
    }
  }, []);

  const deleteInventoryAssignmentRecord = useCallback(async (assignmentId) => {
    try {
      await deleteInventoryAssignment(assignmentId);
      toast.success('Inventory assignment deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting inventory assignment:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to delete inventory assignment';
      toast.error(errorMessage);
      return false;
    }
  }, []);

  const returnInventoryAssignmentRecord = useCallback(async (assignmentId, returnData = {}) => {
    try {
      await returnInventoryAssignment(assignmentId, returnData);
      toast.success('Inventory item returned successfully!');
      return true;
    } catch (error) {
      console.error('Error returning inventory assignment:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to return inventory item';
      toast.error(errorMessage);
      return false;
    }
  }, []);

  const getInventoryAssignmentDetailsRecord = useCallback(async (assignmentId) => {
    try {
      const response = await getInventoryAssignmentDetails(assignmentId);
      return response.data;
    } catch (error) {
      console.error('Error getting inventory assignment details:', error);
      toast.error('Failed to get inventory assignment details');
      return null;
    }
  }, []);

  return {
    isLoading,
    inventoryAssignments,
    stats,
    totalCount,
    filterOptions,
    loadInventoryAssignments,
    loadFilterOptions,
    loadStats,
    createInventoryAssignmentRecord,
    updateInventoryAssignmentRecord,
    deleteInventoryAssignmentRecord,
    returnInventoryAssignmentRecord,
    getInventoryAssignmentDetailsRecord,
  };
};